import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import maplibregl, { GeoJSONSource, LngLatBounds, Map as FleetGLMap } from "maplibre-gl";
import type { FeatureCollection, LineString } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import { Facility, FleetVehicle, validPosition } from "./fleetData";
import { fleetAtBase, fleetDestination, fleetRouteEndpoints } from "./fleetRoute";
import { addFleetLayers, baseRouteSource, clusterLayer, destinationFeatures, emptyCollection, fleetSource, modelLayer, plateLayer, refreshTrailerModels, routeSource, vehicleFeatures, vehicleLayer } from "./fleetMapLayers";
import useFleetEstimates, { FleetLabelFields, fleetLabelFields, fleetMetricLabel } from "./useFleetEstimates";
import { interpolateFleet } from "./fleetMotion";
import { createFleetHover, createBatteryHover } from "./fleetHover";
import { loadRealisticTruckIcons } from "./fleetTruckIcons";
import { syncBaseFlags } from "./fleetBaseFlags";
import baseFlagAsset from "../../assets/fleet/base-flag.png";

const BasicFleetMap = React.lazy(() => import("./BasicFleetMap"));
const center: [number, number] = [73.025, 18.94];
interface Props {
  vehicles: FleetVehicle[]; facilities: Facility[]; selected?: FleetVehicle;
  onSelect: (id: string) => void; trails: Record<string, [number, number][]>;
  live?: boolean; onToggleLive?:()=>void; focusKey?: string;
}
interface RoadRoute { geometry: LineString; distance: number; duration?: number; }
const reducedMotion = () => Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export default function FleetMap(props: Props) {
  const { vehicles, facilities, selected, onSelect,live=true,onToggleLive } = props;
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<FleetGLMap | null>(null);
  const baseFlags=useRef(new Map<string,maplibregl.Marker>());
  const current = useRef({ vehicles, facilities, onSelect });
  current.current = { vehicles, facilities, onSelect };
  const fitted = useRef(false);
  const cameraTouched=useRef(false);
  const fittedRoute = useRef<string>();
  const routeCache = useRef(new Map<string, RoadRoute>());
  const [ready, setReady] = useState(false);
  const [basic, setBasic] = useState(false);
  const [tilted, setTilted] = useState(true);
  const [pitch,setPitch]=useState(65);
  const [realisticIcons,setRealisticIcons]=useState(false);
  const [plates, setPlates] = useState(true);
  const [motion, setMotion] = useState(true);
  useEffect(()=>{if(onToggleLive) setMotion(live);},[live,onToggleLive]);
  const [mapNotice, setMapNotice] = useState("");
  const [routeNotice, setRouteNotice] = useState("");
  const [baseRoutes,setBaseRoutes]=useState<FeatureCollection<LineString>>({type:"FeatureCollection",features:[]});
  const [baseRoutesUnavailable,setBaseRoutesUnavailable]=useState(0);
  const baseRoutesRef=useRef(baseRoutes);baseRoutesRef.current=baseRoutes;
  const receiveBaseRoutes=useCallback((routes:FeatureCollection<LineString>,unavailable:number)=>{setBaseRoutes(routes);setBaseRoutesUnavailable(unavailable);},[]);
  const estimates = useFleetEstimates(vehicles,facilities,ready && !basic,receiveBaseRoutes);
  const labelMetrics = useRef<Record<string,string>>({});
  labelMetrics.current = Object.fromEntries(vehicles.map(vehicle => [vehicle.id,fleetMetricLabel(vehicle,facilities,estimates[vehicle.id])]));
  const labelDetails = useRef<Record<string,FleetLabelFields>>({});
  labelDetails.current = Object.fromEntries(vehicles.map(vehicle => [vehicle.id,fleetLabelFields(vehicle,facilities,estimates[vehicle.id])]));
  const renderedVehicles = useRef<FleetVehicle[]>([]);

  const fit = useCallback((focused=false, trailersOnly=false) => {
    if(!focused) cameraTouched.current=true;
    const instance = map.current;
    if (!instance) return;
    const bounds = new LngLatBounds();
    const fresh=current.current.vehicles.filter(vehicle=>!vehicle.unreachable && validPosition(vehicle.lat,vehicle.lng));
    const fleet=focused && !trailersOnly && fresh.length?fresh:current.current.vehicles;
    fleet.forEach(vehicle => {
      const position = validPosition(vehicle.lat, vehicle.lng);
      if (position) bounds.extend([position.lng, position.lat]);
      const endpoint = fleetDestination(vehicle, current.current.facilities);
      if (!trailersOnly && endpoint && (!focused || endpoint.kind==="base")) bounds.extend([endpoint.lng, endpoint.lat]);
    });
    if (!bounds.isEmpty()) instance.fitBounds(bounds, { pitch:instance.getPitch(),bearing:instance.getBearing(), padding: focused?{top:160,bottom:50,left:30,right:50}:{top:185,bottom:85,left:55,right:55}, maxZoom: focused?16:15, duration: reducedMotion() ? 0 : 650 });
    else instance.easeTo({ center, zoom: 11, duration: reducedMotion() ? 0 : 450 });
  }, []);

  useEffect(() => {
    if (!element.current || basic) return;
    setReady(false);
    setRealisticIcons(false);
    let instance: FleetGLMap;
    let active = true;
    let observer: ResizeObserver | undefined;
    let hoverPopup: maplibregl.Popup | undefined;
    let hoverSignature="";
    let selectionPopup: maplibregl.Popup | undefined;
    try {
      instance = new maplibregl.Map({ container: element.current,
        style: process.env.REACT_APP_FLEET_MAP_STYLE_URL || "https://tiles.openfreemap.org/styles/liberty",
        center, zoom: 11, pitch: 65, bearing: -20, maxPitch: 75, maxZoom: 20,
        attributionControl: { compact: true }, canvasContextAttributes: { antialias: true },
      });
      map.current = instance;
      instance.on("movestart",event=>{
        if(event.originalEvent) {cameraTouched.current=true;fitted.current=true;}
      });
      // The compass should reset north without flattening the tilted map.
      instance.addControl(new maplibregl.NavigationControl({ visualizePitch: false, showZoom:false }), "bottom-right");
      const compass=element.current.querySelector<HTMLButtonElement>(".maplibregl-ctrl-compass");
      let previousOrientation:{bearing:number;pitch:number}|undefined;
      if(compass) {
        compass.setAttribute("aria-label","Toggle north-up and previous view");
        compass.title="North-up / previous angle";
        compass.addEventListener("click",event=>{
          event.preventDefault();event.stopImmediatePropagation();cameraTouched.current=true;
          if(previousOrientation) {
            instance.easeTo({...previousOrientation,duration:reducedMotion()?0:600});
            previousOrientation=undefined;
          } else {
            previousOrientation={bearing:instance.getBearing(),pitch:instance.getPitch()};
            instance.easeTo({bearing:0,pitch:previousOrientation.pitch,duration:reducedMotion()?0:600});
          }
        },true);
      }
      instance.on("pitchend",()=>{
        if(!active) return;
        const actual=Math.round(instance.getPitch());
        setPitch(actual);setTilted(actual>0);
      });
      instance.addControl(new maplibregl.ScaleControl({ maxWidth: 90, unit: "metric" }), "bottom-right");
      instance.on("load", () => {
        if (!active) return;
        addFleetLayers(instance);
        setReady(true); setMapNotice("");
        void loadRealisticTruckIcons(instance,()=>active).then(loaded=>{if(active && loaded) setRealisticIcons(true);});
      });
      instance.on("error", () => {
        if (active) setMapNotice("Some map data could not load. You can use the basic map.");
      });
      const updateModels = () => {
        if (instance.getLayer(modelLayer)) {
          refreshTrailerModels(instance, renderedVehicles.current);
        }
      };
      instance.on("sourcedata", event => { if (event.sourceId === fleetSource && event.isSourceLoaded) updateModels(); });
      instance.on("zoomend", updateModels);
      const showChoices = (ids: string[], location: [number, number]) => {
        const choices = current.current.vehicles.filter(vehicle => ids.includes(vehicle.id));
        if (!choices.length) return;
        hoverPopup?.remove(); hoverSignature = "";
        selectionPopup?.remove();
        const list = document.createElement("div");
        list.className = "fleet-trailer-choices";
        const title = document.createElement("strong");
        title.textContent = `${choices.length} nearby trailers`;
        list.appendChild(title);
        choices.forEach(vehicle => {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = vehicle.plate;
          button.addEventListener("click", () => {
            selectionPopup?.remove();
            current.current.onSelect(vehicle.id);
          });
          list.appendChild(button);
        });
        selectionPopup = new maplibregl.Popup({closeButton:true,closeOnClick:true,offset:24,maxWidth:"230px",className:"fleet-selection-popup"})
          .setLngLat(location).setDOMContent(list).addTo(instance);
      };
      instance.on("click", async event => {
        if (!instance.getLayer(vehicleLayer)) return;
        // A small screen-space tolerance makes narrow trucks and banners easy to hit.
        const {x,y} = event.point;
        const layers = [clusterLayer, vehicleLayer, modelLayer, plateLayer, "fleet-tail-battery"];
        const exact = instance.queryRenderedFeatures(event.point, {layers});
        const hits = exact.length ? exact : instance.queryRenderedFeatures([[x-8,y-8],[x+8,y+8]], {layers});
        const feature = hits.find(hit => hit.properties?.cluster_id != null) || hits[0];
        if (!feature) return;
        hoverPopup?.remove(); hoverSignature = "";
        cameraTouched.current = true;
        if (feature.properties?.cluster_id != null && feature.geometry.type === "Point") {
          try {
            const source = instance.getSource(fleetSource) as GeoJSONSource;
            const clusterId = Number(feature.properties.cluster_id);
            const location = feature.geometry.coordinates as [number,number];
            const [zoom, leaves] = await Promise.all([
              source.getClusterExpansionZoom(clusterId),
              source.getClusterLeaves(clusterId, Number(feature.properties.point_count) || 100, 0).catch(() => []),
            ]);
            if (!active) return;
            instance.easeTo({center:location,zoom:Math.min(Math.max(zoom,instance.getZoom()+1),20),duration:reducedMotion()?0:500});
            showChoices(leaves.map(leaf=>String(leaf.properties?.id)),location);
          } catch { /* A refresh may replace a cluster before its expansion resolves. */ }
        } else if (feature.properties?.id != null) {
          const ids = Array.from(new Set(hits.filter(hit=>hit.properties?.id!=null).map(hit=>String(hit.properties!.id))));
          const vehicle = current.current.vehicles.find(item=>item.id===String(feature.properties!.id));
          if(ids.length>1 && vehicle?.lng!=null && vehicle.lat!=null) showChoices(ids,[vehicle.lng,vehicle.lat]);
          else { selectionPopup?.remove(); current.current.onSelect(String(feature.properties.id)); }
        }
      });
      instance.on("mousemove", event => {
        if (!instance.getLayer(vehicleLayer)) return;
        const target=event.originalEvent?.target as HTMLElement | null;
        if(target?.closest?.(".maplibregl-popup")) return;
        const feature = instance.queryRenderedFeatures(event.point, { layers: [clusterLayer, vehicleLayer, modelLayer, plateLayer, "fleet-tail-battery"] })[0];
        instance.getCanvas().style.cursor = feature ? "pointer" : "";
        const vehicle=feature?.properties?.id!=null?current.current.vehicles.find(item=>item.id===String(feature.properties.id)):undefined;
        if(!vehicle || vehicle.lat==null || vehicle.lng==null) {hoverPopup?.remove();hoverSignature="";return;}
        const batteryHover=feature?.layer?.id === "fleet-tail-battery";
        const signature=JSON.stringify([batteryHover,vehicle,labelDetails.current[vehicle.id]]);
        if(signature===hoverSignature) return;
        hoverSignature=signature;
        if(!hoverPopup) hoverPopup=new maplibregl.Popup({closeButton:false,closeOnClick:false,offset:28,maxWidth:"185px",className:"fleet-hover-popup"});
        hoverPopup.setLngLat([vehicle.lng,vehicle.lat]).setDOMContent(batteryHover?createBatteryHover(vehicle):createFleetHover(vehicle,current.current.facilities,labelDetails.current[vehicle.id])).addTo(instance);
      });
      instance.on("mouseout",event=>{
        const related=event.originalEvent?.relatedTarget as HTMLElement | null;
        if(!related?.closest?.(".fleet-hover-popup")) {hoverPopup?.remove();hoverSignature="";}
      });
      observer = new ResizeObserver(() => instance.resize());
      observer.observe(element.current);
    } catch {
      // Keep tracking usable on machines without WebGL support.
      map.current?.remove(); map.current = null;
      setBasic(true);
    }
    return () => {
      active = false; observer?.disconnect();
      hoverPopup?.remove(); selectionPopup?.remove();
      baseFlags.current.forEach(flag=>flag.remove());baseFlags.current.clear();
      if (instance && map.current === instance) instance.remove();
      map.current = null; fitted.current = false;
    };
  }, [basic]);

  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;
    const source = instance.getSource(fleetSource) as GeoJSONSource | undefined;
    const destinations = instance.getSource("fleet-destinations") as GeoJSONSource | undefined;
    if (!source || !destinations) return;
    destinations.setData(destinationFeatures(vehicles, facilities));
    syncBaseFlags(instance,baseFlags.current,vehicles,facilities,baseFlagAsset);
    const previous = renderedVehicles.current;
    const draw = (fleet: FleetVehicle[]) => {
      renderedVehicles.current = fleet;
      source.setData(vehicleFeatures(fleet,selected?.id,labelMetrics.current,labelDetails.current));
      refreshTrailerModels(instance,fleet);
    };
    if (!fitted.current && vehicles.some(vehicle => validPosition(vehicle.lat, vehicle.lng))) {
      fit(true); fitted.current = true;
    }
    const moved = previous.some(before => vehicles.some(after => before.id===after.id && (before.lat!==after.lat || before.lng!==after.lng)));
    if(!motion || reducedMotion() || !moved) { draw(vehicles); return; }
    const start = performance.now();
    let frame = 0, lastPaint = -Infinity;
    const animate = (time: number) => {
      const progress = Math.min(1,(time-start)/1200);
      if(time-lastPaint>=40 || progress===1) { draw(interpolateFleet(previous,vehicles,progress)); lastPaint=time; }
      if(progress<1) frame=requestAnimationFrame(animate);
    };
    frame=requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [vehicles, facilities, selected?.id, ready, fit, motion]);

  useEffect(() => {
    const source = map.current?.getSource(fleetSource) as GeoJSONSource | undefined;
    if(ready && source) source.setData(vehicleFeatures(renderedVehicles.current,selected?.id,labelMetrics.current,labelDetails.current));
  },[estimates,ready,selected?.id]);

  useEffect(()=>{
    const instance=map.current;
    const source=instance?.getSource(baseRouteSource) as GeoJSONSource | undefined;
    if(!ready || !instance || !source) return;
    source.setData(baseRoutes);
    ["fleet-base-road-casing","fleet-base-road-line"].forEach(id=>instance.setLayoutProperty(id,"visibility",selected?"none":"visible"));
  },[baseRoutes,ready,selected?.id]);

  useEffect(() => {
    if (!ready || !map.current?.getLayer(modelLayer)) return;
    map.current.setLayoutProperty(vehicleLayer,"icon-image",["concat",realisticIcons?"fleet-realistic-":"fleet-truck-",["get","status"]]);
    map.current.setLayoutProperty(vehicleLayer,"icon-size",realisticIcons ? ["interpolate",["linear"],["zoom"],8,.38,13,.48,17,.58,20,.64] : 1.1);
    map.current.setLayoutProperty(vehicleLayer,"icon-pitch-alignment",realisticIcons?"viewport":"map");
    // Rotate the truck around its cab instead of its centre. The slight inset
    // removes transparent sprite padding so the banner foot meets the cab.
    map.current.setLayoutProperty(vehicleLayer,"icon-anchor",realisticIcons?"top":"center");
    map.current.setLayoutProperty(vehicleLayer,"icon-offset",realisticIcons?[0,-20]:[0,0]);

    map.current.setLayoutProperty(vehicleLayer, "visibility", realisticIcons || !tilted ? "visible" : "none");
    map.current.setLayoutProperty(modelLayer, "visibility", !realisticIcons && tilted ? "visible" : "none");

    if (map.current.getLayer("fleet-buildings-3d")) map.current.setLayoutProperty("fleet-buildings-3d", "visibility", tilted ? "visible" : "none");
  }, [tilted, ready,realisticIcons]);

  useEffect(()=>{
    if(ready && map.current) map.current.easeTo({pitch:tilted?pitch:0,duration:reducedMotion()?0:600});
  },[tilted,pitch,ready]);

  useEffect(() => {
    if (ready && map.current?.getLayer(plateLayer)) {
      map.current.setLayoutProperty(plateLayer, "visibility", plates ? "visible" : "none");
    }
  }, [plates, ready]);

  const previousFocusKey = useRef(props.focusKey);
  useEffect(() => {
    if (!ready || previousFocusKey.current === props.focusKey) return;
    previousFocusKey.current = props.focusKey;
    cameraTouched.current = true;
    // Only an explicit status change reframes the map, never a GPS update.
    if (current.current.vehicles.some(vehicle => validPosition(vehicle.lat, vehicle.lng))) {
      fit(true, true);
      fitted.current = true;
    }
  }, [props.focusKey, ready, fit]);

  const selectedId = selected?.id;
  // A live fix updates the source without resetting the operator's camera.
  useEffect(() => {
    if (!ready || !selectedId) { fittedRoute.current = undefined; return; }
    cameraTouched.current=false;
    const vehicle = current.current.vehicles.find(item => item.id === selectedId);
    const position = validPosition(vehicle?.lat, vehicle?.lng);
    if (position) map.current?.easeTo({ center: [position.lng, position.lat], zoom: Math.max(map.current.getZoom(), 14), duration: reducedMotion() ? 0 : 650 });
  }, [selectedId, ready]);

  const endpoints = selected ? fleetRouteEndpoints(selected, facilities) : undefined;
  const routeKey = endpoints ? JSON.stringify(endpoints) : undefined;
  const atBase = selected ? fleetAtBase(selected, facilities) : false;
  const stale = Boolean(selected?.unreachable);
  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;
    const source = instance.getSource(routeSource) as GeoJSONSource | undefined;
    if (!source) return;
    source.setData(emptyCollection); setRouteNotice("");
    if (!selectedId) return;
    if (atBase) { setRouteNotice(stale ? "Last known position is within 500 m of base" : "At base · Within 500 m"); return; }
    if (!routeKey) { setRouteNotice("Road route unavailable: endpoint coordinates are missing."); return; }
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const show = (route: RoadRoute) => {
      if (!active) return;
      const data: FeatureCollection<LineString> = { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: route.geometry }] };
      source.setData(data);
      if (fittedRoute.current !== selectedId && !cameraTouched.current) {
        const bounds = new LngLatBounds();
        route.geometry.coordinates.forEach(point => bounds.extend(point as [number, number]));
        instance.fitBounds(bounds, { pitch:instance.getPitch(),bearing:instance.getBearing(), padding: { top: 185, bottom: 85, left: 55, right: 55 }, maxZoom: 15, duration: reducedMotion() ? 0 : 650 });
        fittedRoute.current = selectedId;
      }
      fittedRoute.current=selectedId;
      setRouteNotice((stale ? "Last known position · " : "") + "Road route · " + (route.distance / 1000).toFixed(1) + " km" +
        (route.duration != null ? " · ~" + Math.ceil(route.duration / 60) + " min" : "") + " · Excludes traffic and truck restrictions");
    };
    const [routeStart,routeEnd]=JSON.parse(routeKey) as NonNullable<typeof endpoints>;
    const sharedKey=JSON.stringify([routeStart.lng,routeStart.lat,routeEnd.lng,routeEnd.lat]);
    const shared=baseRoutesRef.current.features.find(feature=>feature.properties?.key===sharedKey);
    const sharedRoute:RoadRoute | undefined=shared && typeof shared.properties?.distance==="number"?{geometry:shared.geometry,distance:shared.properties.distance,duration:shared.properties.duration}:undefined;
    const cached = routeCache.current.get(routeKey) || sharedRoute;
    if (cached) { show(cached); clearTimeout(timeout); }
    else {
      setRouteNotice("Loading road route…");
      const [start, end] = JSON.parse(routeKey) as NonNullable<typeof endpoints>;
      const routingUrl = (process.env.REACT_APP_FLEET_ROUTING_URL || "https://router.project-osrm.org").replace(/\/$/, "");
      fetch(`${routingUrl}/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson`, { signal: controller.signal })
        .then(async response => {
          if (!response.ok) throw new Error("Route unavailable");
          const data = await response.json();
          const route = data.routes?.[0];
          if (data.code !== "Ok" || route?.geometry?.type !== "LineString" || !Array.isArray(route.geometry.coordinates) || route.geometry.coordinates.length < 2 ||
            !Number.isFinite(route.distance) || route.distance < 0 || route.geometry.coordinates.some((point: number[]) => !Array.isArray(point) || !validPosition(point[1], point[0]))) throw new Error("Invalid road route");
          const result: RoadRoute = { geometry: route.geometry, distance: route.distance,
            duration: Number.isFinite(route.duration) && route.duration >= 0 ? route.duration : undefined };
          if (!active) return;
          if (routeCache.current.size >= 50) routeCache.current.clear();
          routeCache.current.set(routeKey, result); show(result);
        })
        .catch(() => { if (active) setRouteNotice("Road route unavailable. Close and select the trailer to retry."); })
        .finally(() => clearTimeout(timeout));
    }
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [selectedId, routeKey, atBase, stale, ready]);

  if (basic) return <Suspense fallback={<div className="fleet-no-fixes" role="status">Loading basic map…</div>}><BasicFleetMap {...props} /></Suspense>;
  return <>
    <div ref={element} className="fleet-map-canvas fleet-vector-map" aria-label="Live fleet map. Select a truck or a cluster; trailer details are also available in the fleet list." />
    {!ready && !mapNotice && <div className="fleet-map-loading" role="status">Loading fleet map…</div>}
    {(routeNotice || (!selected && baseRoutesUnavailable>0)) && <div className="fleet-route-notice" role="status">{routeNotice || `${baseRoutesUnavailable} base route${baseRoutesUnavailable===1?"":"s"} unavailable · Retrying automatically`}</div>}
    {mapNotice && <div className="fleet-map-notice" role="status">{mapNotice}<button onClick={() => { setReady(false); setBasic(true); }}>Use basic map</button></div>}
    <div className="fleet-map-tools fleet-vector-tools" role="toolbar" aria-label="Map tools">
      <button onClick={()=>{cameraTouched.current=true;map.current?.zoomIn();}} disabled={!ready} aria-label="Zoom in" title="Zoom in">+</button>
      <button onClick={()=>{cameraTouched.current=true;map.current?.zoomOut();}} disabled={!ready} aria-label="Zoom out" title="Zoom out">−</button>
      <button onClick={()=>fit()} disabled={!ready} aria-label="Show the whole fleet" title="Show the whole fleet">⛶</button>
      <button onClick={() => {if(!tilted && pitch===0) setPitch(65);setTilted(value => !value);}} disabled={!ready} aria-label="Tilt map in 3D" aria-pressed={tilted} title="Toggle 2D / 3D">3D</button>
      <button onClick={() => setPlates(value => !value)} disabled={!ready} aria-label="Show vehicle numbers" aria-pressed={plates} title="Show vehicle numbers">ID</button>
      <button onClick={() => onToggleLive?onToggleLive():setMotion(value => !value)} disabled={!ready} aria-label={onToggleLive?(live?"Pause live tracking":"Resume live tracking"):"Animate GPS updates"} aria-pressed={onToggleLive?live:motion} title={onToggleLive?(live?"Pause live GPS updates":"Resume live GPS updates and refresh now"):"Animate GPS updates"}>{(onToggleLive?live:motion)?"Ⅱ":"▶"}</button>
    </div>
    <div className="fleet-pitch-control" role="group" aria-label="Map tilt angle">
      <span>Tilt</span>
      <button type="button" aria-label="Decrease tilt angle" disabled={!ready || !tilted || pitch<=0} onClick={()=>{cameraTouched.current=true;const value=Math.max(0,(tilted?pitch:0)-5);setPitch(value);setTilted(value>0);}}>−</button>
      <output aria-live="polite">{tilted?pitch:0}°</output>
      <button type="button" aria-label="Increase tilt angle" disabled={!ready || (tilted && pitch>=75)} onClick={()=>{cameraTouched.current=true;const value=Math.min(75,(tilted?pitch:0)+5);setPitch(value);setTilted(value>0);}}>+</button>
    </div>
    <div className="fleet-map-legend fleet-vector-legend"><span><i className="running" />Running</span><span><i className="idle" />Idle</span><span><i className="halted" />Halted</span><span><i className="unknown" />Unknown</span><span><i className="fleet-stale-dot" />Last known</span><span className="fleet-map-legend-hint">Distance / time → base or trip destination · Excludes traffic</span></div>
  </>;
}
